import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Box, Typography
} from '@mui/material';
import { getAllFolders } from '../../api/folderAPI';
import { useTranslation } from 'react-i18next';
import { FolderData } from '../../types/types';
import SecurityIcon from '@mui/icons-material/Security';
import { updateFolderAccess, getFolderAccess } from '../../api/folderAPI';

interface Props {
  open: boolean;
  onClose: () => void;
  user: {
    email: string;
    userId: string;
  };
}

const ModalFolderPermissions: React.FC<Props> = ({ open, onClose, user }) => {
  const { t } = useTranslation();
  const [folders, setFolders] = useState<FolderData[]>([]);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const folderData = await getAllFolders();
        setFolders(folderData);

        const accessData = await getFolderAccess(user.userId);
        const mappedPermissions = accessData.reduce((acc: Record<string, boolean>, entry: any) => {
          acc[entry.folderId] = entry.access;
          return acc;
        }, {});
        setPermissions(mappedPermissions);
      } catch (error) {
        console.error('Error loading folder permissions', error);
      }
    };

    if (open) fetchData();
  }, [open]);

  const flattenFolders = (folders: FolderData[], level = 0): { folderId: string, name: string, level: number }[] => {
    return folders.flatMap(folder => [
      { folderId: folder.folderId, name: folder.folderName, level },
      ...flattenFolders(folder.subfolders || [], level + 1)
    ]);
  };

  const handlePermissionChange = (folderId: string) => {
    setPermissions(prev => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const handleSave = async () => {
    try {
      const permissionsArray = Object.entries(permissions).map(([folderId, access]) => ({
        folderId,
        access,
      }));

      await updateFolderAccess(user.userId, permissionsArray);
      onClose();
    } catch (err) {
      console.error('Error updating folder access', err);
    }
  };

  const folderList = flattenFolders(folders);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <SecurityIcon sx={{ marginRight: 1, color: '#73AF73' }} />
          <Typography variant="h6" sx={{ color: '#73AF73' }}>{t('folderPermissionsFor')} {user.email}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>{t('folder')}</b></TableCell>
              <TableCell><b>{t('access')}</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {folderList.map(folder => (
              <TableRow key={folder.folderId}>
                <TableCell sx={{ pl: folder.level * 2 }}>{folder.name}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={!!permissions[folder.folderId]} // true o false
                    onChange={() => handlePermissionChange(folder.folderId)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button
            variant="outlined"
            onClick={onClose}
            sx={{
                color: '#d32f2f',
                borderColor: '#d32f2f',
                '&:hover': {
                backgroundColor: '#fddede', 
                borderColor: '#d32f2f',
                },
            }}
            >
            {t('cancel')}
        </Button>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#73AF73' }}
          onClick={handleSave}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalFolderPermissions;